import { HL7AckProcessing, AdditionalVisitDto } from '@lmt/shared-types';
import { SchemaOf } from 'yup';
import * as yup from 'yup';
import { HL7SettingsData } from './types';
import i18next from 'i18next';

function t(value: string, options?: any) {
  return i18next.t(`translation:errors.${value}` as any, options);
}

export const additionalVisitYupSchema: yup.SchemaOf<AdditionalVisitDto> =
  yup.object({
    type: yup.string().required(() => t('required')),
    visitStartTimeInSecondsSinceMidnight: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(0, () => t('min_number', { minNumber: 0 }))
      .required(() => t('required')),
    visitLengthInSeconds: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(0, () => t('min_number', { minNumber: 0 }))
      .required(() => t('required')),
    visitTypeId: yup.number().required(() => t('required')),
  });

export const hl7SettingsDataYupSchema: SchemaOf<Partial<HL7SettingsData>> =
  yup.object({
    // General
    onlyProcessMessagesForExistingPatients: yup.boolean(),
    subtenantId: yup.number(),
    ackProcessing: yup.mixed<HL7AckProcessing>().required(),
    facility: yup.string(),
    appName: yup.string(),
    encoding: yup.string(),
    // Incoming settings
    incomingTransporterEnabled: yup.boolean(),
    defaultVisitTypeId: yup.number(),
    visitStartTimeInSecondsSinceMidnight: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('overwriteTime', {
        is: true,
        then: yup
          .number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .required(() => t('required')),
      }),
    visitLengthInHours: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    visitSearchBackwardDaysCount: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    visitSearchForwardDaysCount: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    caseSearchBackwardDaysCount: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    caseSearchForwardDaysCount: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    additionalVisits: yup.array().of(
      yup
        .object()
        .shape({
          type: yup.string().required(),
          visitStartTimeInSecondsSinceMidnight: yup
            .number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .min(0)
            .required(),
          visitLengthInSeconds: yup.number().min(0).required(),
          visitTypeId: yup.number().required(),
        })
        .defined(),
    ),
    overwriteTime: yup.boolean(),
    overwriteTimeOption: yup.mixed() as any,
    // Outgoing settings
    outgoingTransporterEnabled: yup.boolean(),
    isHl7OutgoingEnabled: yup.boolean(),
    serverAddress: yup.string().when('isHl7OutgoingEnabled', {
      is: (value: any) => !!value,
      then: yup.string().required(),
    }),
    /** Delays to retry failing sending attempts (in seconds)  */
    retryDelays: yup.string().when('isHl7OutgoingEnabled', {
      is: (value: any) => !!value,
      then: yup.string().required(),
    }),
    serverPort: yup.number().when('isHl7OutgoingEnabled', {
      is: (value: any) => !!value,
      then: yup
        .number()
        .typeError(() =>
          i18next.t('translation:tenant_settings.hl7.port_error_message'),
        )
        .required(),
    }),
    filesDirectory: yup.string().when(['isHl7OutgoingEnabled'], {
      is: (isHl7OutgoingEnabled: boolean) => isHl7OutgoingEnabled,
      then: yup.string().required(),
    }),
  });

export function validateField(key: string, value: any): string | undefined {
  try {
    yup.reach(additionalVisitYupSchema, key).validateSync(value);
    return undefined;
  } catch (error: any) {
    return error.message as string;
  }
}
